<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Chart from 'primevue/chart';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import type { ChartOptions, TooltipItem } from 'chart.js';
import { useEndpoint } from '@apparatix/composables';
import { Section } from '@apparatix/library/core';
import type {
  InventoryMeasurementOptions,
  InventoryMeasurementProfile,
  InventoryMeasurementsResponse,
  MeasurementHistoryResponse,
  MeasurementHistorySeries,
} from './types';

const props = defineProps<{ id: number }>();

type EditableRow = {
  key: string;
  providerCode: string;
  providerName: string;
  metricCode: string;
  metricName: string;
  segmentCode: string;
  segmentName: string;
  value: number;
  grain: string;
  quality: string | null;
  originalValue: number;
  originalGrain: string;
  isNew: boolean;
  // Provider-supplied values are locked until the user explicitly overrides them.
  overrideAcknowledged: boolean;
};

const PROVIDER_SUPPLIED_QUALITY = 'provider_supplied';

const GRAIN_OPTIONS = [
  { label: 'Weekly', value: 'week' },
  { label: 'Daily', value: 'day' },
];

const rows = ref<EditableRow[]>([]);
const options = ref<InventoryMeasurementOptions>({ providers: [], metrics: [], segments: []});

// New-measurement form state.
const newProvider = ref<string | null>(null);
const newMetric = ref<string | null>(null);
const newSegment = ref<string | null>(null);

const toRow = (profile: InventoryMeasurementProfile): EditableRow => ({
  key: `${profile.providerCode}|${profile.metricCode}|${profile.segmentCode}`,
  providerCode: profile.providerCode,
  providerName: profile.providerName,
  metricCode: profile.metricCode,
  metricName: profile.metricName,
  segmentCode: profile.segmentCode,
  segmentName: profile.segmentName,
  value: profile.value,
  grain: profile.grain === 'day' ? 'day' : 'week',
  quality: profile.quality,
  originalValue: profile.value,
  originalGrain: profile.grain === 'day' ? 'day' : 'week',
  isNew: false,
  overrideAcknowledged: false,
});

const applyResponse = (data: InventoryMeasurementsResponse) => {
  options.value = data.options;
  rows.value = data.profiles.map(toRow);
  newProvider.value = null;
  newMetric.value = null;
  newSegment.value = null;

  // Seed the chart selectors once: prefer an existing profile's combo so the
  // chart shows data immediately, falling back to the first available option.
  if (chartProvider.value === null) {
    const seed = data.profiles[0];
    chartProvider.value = seed?.providerCode ?? data.options.providers[0]?.code ?? null;
    chartMetric.value = seed?.metricCode ?? data.options.metrics[0]?.code ?? null;
  }
};

const { isLoading, makeRequest, setEndpointUrl } = useEndpoint<{ data: InventoryMeasurementsResponse }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => applyResponse(response.data),
  toast: { showOnSuccess: false },
});

const {
  isLoading: isSaving,
  makeRequest: saveRequest,
  setEndpointUrl: setSaveEndpoint,
} = useEndpoint<{ data: InventoryMeasurementsResponse }>({
  url: '',
  type: 'POST',
  successField: 'data',
  onSuccess: response => applyResponse(response.data),
  toast: { showOnSuccess: true, successMessage: 'Measurements saved' },
});

const confirm = useConfirm();

// A provider-supplied value stays locked until the user explicitly acknowledges
// that they're overriding it with a manual value.
const isLockedProviderRow = (row: EditableRow): boolean =>
  row.quality === PROVIDER_SUPPLIED_QUALITY && !row.overrideAcknowledged;

const confirmOverride = (row: EditableRow): void => {
  confirm.require({
    group: 'measurement-provider-override',
    header: 'Override provider-supplied value?',
    message:
      'This value was supplied by the data provider. Editing it replaces it with a manual value. Are you sure you want to override it?',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: 'Cancel', size: 'small', severity: 'secondary' },
    acceptProps: { label: 'Override', size: 'small', severity: 'warn' },
    accept: () => {
      row.overrideAcknowledged = true;
    },
  });
};

const dirtyRows = computed(() =>
  rows.value.filter(row => row.isNew || row.value !== row.originalValue || row.grain !== row.originalGrain),
);

const hasChanges = computed(() => dirtyRows.value.length > 0);

const canAdd = computed(() => !!newProvider.value && !!newMetric.value && !!newSegment.value);

const nameFor = (list: { code: string; name: string }[], code: string) =>
  list.find(item => item.code === code)?.name ?? code;

const addRow = () => {
  if (!canAdd.value) {
    return;
  }

  const key = `${newProvider.value}|${newMetric.value}|${newSegment.value}`;

  if (rows.value.some(row => row.key === key)) {
    // Already present — surface the existing row rather than duplicating it.
    return;
  }

  rows.value.unshift({
    key,
    providerCode: newProvider.value as string,
    providerName: nameFor(options.value.providers, newProvider.value as string),
    metricCode: newMetric.value as string,
    metricName: nameFor(options.value.metrics, newMetric.value as string),
    segmentCode: newSegment.value as string,
    segmentName: nameFor(options.value.segments, newSegment.value as string),
    value: 0,
    grain: 'week',
    quality: null,
    originalValue: NaN,
    originalGrain: '',
    isNew: true,
    overrideAcknowledged: true,
  });

  newProvider.value = null;
  newMetric.value = null;
  newSegment.value = null;
};

const save = () => {
  if (!hasChanges.value) {
    return;
  }

  saveRequest({
    measurements: dirtyRows.value.map(row => ({
      provider: row.providerCode,
      metric: row.metricCode,
      segment: row.segmentCode,
      value: row.value,
      grain: row.grain,
    })),
  });
};

// --- Value history chart -------------------------------------------------

const GRAIN_DISPLAY_OPTIONS = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Daily', value: 'daily' },
  { label: '4 Week', value: 'four_week' },
];

// Distinct line color per audience segment; cycles if there are more audiences.
const SERIES_COLORS = [
  '#3B82F6', // blue
  '#F97316', // orange
  '#10B981', // green
  '#8B5CF6', // purple
  '#EF4444', // red
  '#14B8A6', // teal
  '#EC4899', // pink
  '#EAB308', // amber
  '#6366F1', // indigo
  '#84CC16', // lime
];

const chartProvider = ref<string | null>(null);
const chartMetric = ref<string | null>(null);
const chartGrain = ref<string>('weekly');
const historySeries = ref<MeasurementHistorySeries[]>([]);

const {
  isLoading: isHistoryLoading,
  makeRequest: historyRequest,
  setEndpointUrl: setHistoryEndpoint,
} = useEndpoint<{ data: MeasurementHistoryResponse }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    historySeries.value = response.data.series;
  },
  toast: { showOnSuccess: false },
});

const canChart = computed(() => !!chartProvider.value && !!chartMetric.value);

const hasHistory = computed(() => historySeries.value.some(series => series.points.length > 0));

const fetchHistory = () => {
  if (!canChart.value) {
    historySeries.value = [];
    return;
  }

  const params = new URLSearchParams({
    provider: chartProvider.value as string,
    metric: chartMetric.value as string,
    grain: chartGrain.value,
  });

  setHistoryEndpoint(`/manage-measurements/inventory/${props.id}/measurement-history?${params.toString()}`);
  historyRequest();
};

watch([chartProvider, chartMetric, chartGrain], fetchHistory);

// Today's date in the same Y-m-d format the backend emits for points, so the
// chart axis can always run through the present day.
const todayDate = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});

// Shared category axis: the sorted union of every series' effective dates, always
// extended through today so a series with a single (or stale) data point still
// runs to the present instead of appearing to cut off. Y-m-d strings sort
// chronologically, so a plain sort keeps them ordered.
const chartLabels = computed(() => {
  const dates = new Set<string>();
  historySeries.value.forEach(series => series.points.forEach(point => dates.add(point.date)));
  if (dates.size > 0) {
    dates.add(todayDate.value);
  }
  return Array.from(dates).sort();
});

const chartData = computed(() => ({
  labels: chartLabels.value,
  datasets: historySeries.value.map((series, index) => {
    const color = SERIES_COLORS[index % SERIES_COLORS.length];
    const byDate = new Map(series.points.map(point => [point.date, point.value]));
    // Measurement values hold until changed, so carry the most recent value
    // forward across any trailing labels (other series' later dates, and today)
    // — the line then continues flat to the present rather than ending early.
    const lastPoint = series.points.at(-1) ?? null;

    return {
      label: series.segmentName,
      data: chartLabels.value.map(date => {
        const value = byDate.get(date);
        if (value !== undefined) {
          return value;
        }
        if (lastPoint && date > lastPoint.date) {
          return lastPoint.value;
        }
        return null;
      }),
      fill: false,
      borderColor: color,
      backgroundColor: color,
      tension: 0.4,
      spanGaps: true,
    };
  }),
}));

const chartOptions = computed<ChartOptions<'line'>>(() => {
  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
  const surfaceBorder = documentStyle.getPropertyValue('--surface-950');
  const labels = chartLabels.value;
  // Per-series lookup of date -> point for tooltip metadata (who/what + when).
  const seriesMaps = historySeries.value.map(series => new Map(series.points.map(point => [point.date, point])));

  return {
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    plugins: {
      legend: { labels: { color: textColor }},
      tooltip: {
        callbacks: {
          label: ctx => ` ${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString()}`,
          afterLabel: (ctx: TooltipItem<'line'>) => {
            const point = seriesMaps[ctx.datasetIndex]?.get(labels[ctx.dataIndex]);
            if (!point) {
              return '';
            }
            const lines = [`Set by: ${point.attribution}`];
            if (point.changedAt) {
              lines.push(`On: ${point.changedAt}`);
            }
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: textColorSecondary },
        grid: { color: surfaceBorder },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          callback: value => Number(value).toLocaleString(),
        },
        grid: { color: surfaceBorder },
      },
    },
  };
});

// Load (or reload) the unit. The flyout reuses this component instance when the
// selected inventory id changes without remounting it, so this must run on every
// props.id change, not just onMounted. Reset the chart selectors (they are only
// seeded while null in applyResponse) and clear any prior series so the chart
// re-seeds and refetches for the new unit rather than showing the previous one.
const loadUnit = () => {
  chartProvider.value = null;
  chartMetric.value = null;
  historySeries.value = [];

  setEndpointUrl(`/manage-measurements/inventory/${props.id}/measurements`);
  setSaveEndpoint(`/manage-measurements/inventory/${props.id}/measurements`);
  makeRequest();
};

watch(() => props.id, loadUnit);
onMounted(loadUnit);
</script>
<template>
  <div class="flex flex-col gap-4">
    <div v-if="isLoading" class="flex justify-center py-10">
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
    </div>
    <template v-else>
      <Section :footer="false">
        <div class="flex flex-wrap items-end gap-3 mb-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-gray-500 uppercase">Provider</label>
            <Select
              v-model="newProvider"
              :options="options.providers"
              option-label="name"
              option-value="code"
              placeholder="Provider"
              class="w-44"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-gray-500 uppercase">Metric</label>
            <Select
              v-model="newMetric"
              :options="options.metrics"
              option-label="name"
              option-value="code"
              placeholder="Metric"
              class="w-44"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-gray-500 uppercase">Audience</label>
            <Select
              v-model="newSegment"
              :options="options.segments"
              option-label="name"
              option-value="code"
              placeholder="Audience"
              class="w-36"
            />
          </div>
          <Button
            label="Add"
            icon="pi pi-plus"
            severity="secondary"
            :disabled="!canAdd"
            @click="addRow"
          />
        </div>

        <DataTable
          :value="rows"
          data-key="key"
          size="small"
          class="text-sm"
        >
          <Column field="providerName" header="Provider" sortable />
          <Column field="metricName" header="Metric" sortable />
          <Column field="segmentName" header="Audience" sortable />
          <Column header="Value" style="width: 14rem">
            <template #body="{ data }">
              <div class="flex items-center gap-2">
                <InputNumber
                  v-model="data.value"
                  :max-fraction-digits="4"
                  :min-fraction-digits="0"
                  :disabled="isLockedProviderRow(data)"
                  class="w-full"
                  :input-class="data.value !== data.originalValue || data.isNew ? 'font-bold' : ''"
                />
                <Button
                  v-if="isLockedProviderRow(data)"
                  v-tooltip.top="'Provider-supplied value — click to override'"
                  icon="pi pi-lock"
                  severity="warn"
                  text
                  rounded
                  aria-label="Override provider-supplied value"
                  @click="confirmOverride(data)"
                />
              </div>
            </template>
          </Column>
          <Column header="Grain" style="width: 9rem">
            <template #body="{ data }">
              <Select
                v-model="data.grain"
                :options="GRAIN_OPTIONS"
                option-label="label"
                option-value="value"
                :disabled="isLockedProviderRow(data)"
                class="w-full"
              />
            </template>
          </Column>
          <template #empty>
            <div class="py-4 text-center text-gray-500">
              No measurements recorded for this inventory unit yet.
            </div>
          </template>
        </DataTable>

        <div class="flex justify-end mt-4">
          <Button
            label="Save"
            icon="pi pi-save"
            :loading="isSaving"
            :disabled="!hasChanges"
            @click="save"
          />
        </div>
      </Section>

      <Section :footer="false">
        <template #title>
          <h3 class="text-base font-bold text-gray-700">
            History
          </h3>
        </template>

        <div class="flex flex-wrap items-end gap-3 mb-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-gray-500 uppercase">Provider</label>
            <Select
              v-model="chartProvider"
              :options="options.providers"
              option-label="name"
              option-value="code"
              placeholder="Provider"
              class="w-44"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-gray-500 uppercase">Metric</label>
            <Select
              v-model="chartMetric"
              :options="options.metrics"
              option-label="name"
              option-value="code"
              placeholder="Metric"
              class="w-44"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-bold text-gray-500 uppercase">Grain</label>
            <Select
              v-model="chartGrain"
              :options="GRAIN_DISPLAY_OPTIONS"
              option-label="label"
              option-value="value"
              class="w-36"
            />
          </div>
        </div>

        <div v-if="isHistoryLoading" class="flex justify-center py-10">
          <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
        </div>
        <div v-else-if="!canChart" class="py-10 text-center text-gray-500">
          Select a provider, metric, and grain to view value history.
        </div>
        <div v-else-if="!hasHistory" class="py-10 text-center text-gray-500">
          No history recorded for this selection yet.
        </div>
        <div v-else class="w-full" style="position: relative; height: 22rem">
          <Chart
            type="line"
            :data="chartData"
            :options="chartOptions"
          />
        </div>
      </Section>
    </template>

    <ConfirmDialog group="measurement-provider-override" />
  </div>
</template>
